import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { HelpCircle } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { MediaMonitoringSubNav } from "@/components/site/MediaMonitoringSubNav";
import { FaqSearch } from "@/components/site/FaqSearch";
import { RequestDemoInlineForm } from "@/components/site/RequestDemoInlineForm";
import { platformFaqs } from "@/content/faqs";

import { pageSchemas } from "@/lib/seo/page-schema";
const META_DESCRIPTION =
  "Questions about Perceptica's media monitoring platform and media listening platform - pricing, sources, languages, exports and onboarding. Answers inside.";

export const Route = createFileRoute("/media-monitoring/platform_/faq")({
  head: () => ({
    meta: [
      { title: "Media Monitoring Platform FAQ | Perceptica" },
      { name: "description", content: META_DESCRIPTION },
      { property: "og:title", content: "Media Monitoring Platform FAQ | Perceptica" },
      { property: "og:description", content: META_DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Media Monitoring Platform FAQ | Perceptica" },
      { name: "twitter:description", content: META_DESCRIPTION },
      { property: "og:url", content: "https://perceptica.com/media-monitoring/platform/faq" },
      ...ogImageMeta("en", "/media-monitoring/platform/faq"),
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/media-monitoring/platform/faq", faq: platformFaqs }),
  }),
  component: PlatformFaqPage,
});

function PlatformFaqPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main">
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          <div className="grid-overlay" />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 right-0 h-[420px] w-[420px] rounded-full opacity-30 blur-[140px]"
            style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
          />
          <div className="noise-overlay" />
          <div className="relative mx-auto max-w-[1100px] px-6 py-20 lg:py-28">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
              <HelpCircle className="h-3.5 w-3.5" /> Platform FAQ
            </span>
            <h1 className="mt-5 max-w-[28ch] font-display text-[clamp(2.25rem,4.4vw,3.25rem)] font-medium leading-[1.05] text-white">
              Everything you might want to know about MLP 2.0
            </h1>
            <p className="mt-6 max-w-2xl text-[15.5px] leading-relaxed text-white/70">
              Coverage, sentiment, alerts, exports and access - the most common questions our team is asked about the Perceptica Media Listening Platform.
            </p>
          </div>
        </section>

        <MediaMonitoringSubNav />

        <section className="py-20 lg:py-24">
          <div className="mx-auto max-w-[900px] px-6 lg:px-10">
            <FaqSearch items={platformFaqs} idPrefix="platform-faq" />
          </div>
        </section>

        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto grid max-w-[1100px] items-center gap-12 px-6 py-20 lg:grid-cols-[1fr_1.1fr] lg:py-24">
            <div className="text-center lg:text-left">
              <h2 className="font-display max-w-[26ch] text-[clamp(1.75rem,3vw,2.5rem)] text-white max-lg:mx-auto">
                Still have questions about the platform?
              </h2>
              <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/70 max-lg:mx-auto">
                Book a personalised demo and our team will walk you through MLP 2.0 with your brand, competitors and topics already loaded in.
              </p>
              <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
                Most requests receive a reply from us within one business day.
              </p>
            </div>
            <RequestDemoInlineForm formName="request-a-demo-platform-faq" />
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
